import { Box, Typography } from "@mui/material";
import React from "react";

import { IncidentDetail } from "../../types/incidentDetail";
import type { EntityDetailMode } from "../../types/entityRegistry";
import type { DetailSection } from "../Detail/DetailSections";
import GroupedRelationList from "../Detail/GroupedRelationList";
import SourceTable from "../Detail/SourceTable";
import IncidentOverview, { hasIncidentOverview } from "./IncidentOverview";
import IncidentProductTable from "./IncidentProductTable";
import {
  countRelations,
  linkGroups,
  productGroups,
} from "./incidentRelations";

// A count for the strip, where the section holds anything to count
const countOf = (count: number): number | undefined =>
  count > 0 ? count : undefined;

/**
 * The sections of the incident view, in the order shown. A section the
 * incident has nothing in has no content, and so is left out of the view.
 */
export const incidentSections = (
  incident: IncidentDetail,
  mode: EntityDetailMode,
): DetailSection[] => {
  const products = productGroups(incident.products);
  const productCount = countRelations(products);

  const kinds = [
    { title: "Organizations", groups: linkGroups(incident.organizations) },
    { title: "Facilities", groups: linkGroups(incident.facilities) },
    { title: "Projects", groups: linkGroups(incident.projects) },
  ];
  const linkCount = kinds.reduce(
    (count, kind) => count + countRelations(kind.groups),
    0,
  );

  return [
    {
      id: "overview",
      label: "Overview",
      content: hasIncidentOverview(incident) && (
        <IncidentOverview incident={incident} />
      ),
    },
    {
      id: "products",
      label: "Products involved",
      count: countOf(productCount),
      content: productCount > 0 && <IncidentProductTable groups={products} />,
    },
    {
      id: "context",
      label: "Organizations, facilities and projects",
      count: countOf(linkCount),
      content: linkCount > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {kinds
            .filter((kind) => countRelations(kind.groups) > 0)
            .map((kind) => (
              <Box key={kind.title}>
                <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                  {kind.title}
                </Typography>
                <GroupedRelationList groups={kind.groups} columns={2} />
              </Box>
            ))}
        </Box>
      ),
    },
    {
      id: "sources",
      label: "Sources",
      count: countOf(incident.sources.total),
      content: incident.sources.items.length > 0 && (
        <SourceTable
          sources={incident.sources.items}
          total={incident.sources.total}
          mode={mode}
        />
      ),
    },
  ];
};
